import { Card, Heading, Paragraph, Tag } from "@digdir/designsystemet-react";
import { CheckmarkIcon } from "@navikt/aksel-icons";
import { useMutation } from "@tanstack/react-query";
import { type ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import type {
  OppgjørForPeriode,
  ValidationProblemDetails,
} from "@/api/generated";
import { postInnrapporteringSimulateMutation } from "@/api/generated/@tanstack/react-query.gen";
import { Page } from "@/components";
import { UploadFileButton } from "@/components/UploadFileButton";
import { ValidationProblems } from "@/components/ValidationProblems";
import { DokumentasjonsLenker } from "@/features/dokumentasjon/DokumentasjonsLenker";
import { useSelskaper } from "@/features/selskap/SelskapContext";
import { useToaster } from "@/features/toaster";
import { cn } from "@/utils";
import { MaaledataPeriodeOppsummering } from "../periode/MaaledataPeriodeOppsummering";

export const MaaledataSimuleringPage = () => {
  return (
    <Page withBackLink backLinkTo={"history"}>
      <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
        <div className="max-w-card space-y-6">
          <HeaderSection />
          <SimuleringFormSection />
        </div>
        <SimuleringSection className="max-w-small-card mt-4 lg:m-0" />
      </div>
    </Page>
  );
};

const SimuleringSection = ({ className }: { className?: string }) => {
  return (
    <div className={cn("", className)}>
      <DokumentasjonsLenker className="lg:self-end" />
    </div>
  );
};

const HeaderSection = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  return (
    <div className={cn("", className)}>
      <Tag className="mb-2 bg-balder-support-surface-tinted! text-balder-text-support-default!">
        {t("pages.simulering.tag")}
      </Tag>
      <Heading data-size={"lg"} className="mb-4">
        {t("pages.simulering.tittel")}
      </Heading>
      <Paragraph>{t("pages.simulering.description")}</Paragraph>
    </div>
  );
};

const SimuleringFormSection = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  const validationMutation = useMutation({
    ...postInnrapporteringSimulateMutation({
      client: httpClient,
    }),
  });
  const toaster = useToaster();

  const [uploadedFileIsValid, setUploadedFileIsValid] = useState<
    boolean | null
  >(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [fileUploadError, setFileUploadError] =
    useState<ValidationProblemDetails | null>(null);
  const [validationResponse, setValidationResponse] =
    useState<OppgjørForPeriode | null>(null);
  const { active } = useSelskaper();

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      throw new Error("No file selected");
    }
    if (!active?.organisasjonsnummer) {
      return;
    }

    setFileUploadError(null);
    setFileUploadLoading(true);
    setUploadedFileName(file.name);
    setUploadedFileIsValid(null);
    setValidationResponse(null);

    validationMutation.mutate(
      {
        body: { file: file },
        path: { organisasjonsNummer: active.organisasjonsnummer },
        headers: { "Content-Type": "multipart/form-data" },
      },
      {
        onError: (error) => {
          const problemDetails =
            error.response?.data ??
            ({
              message: t("pages.simulering.opplasting-feil", {
                context: "opplasting",
              }),
            } as ValidationProblemDetails);
          setFileUploadError(problemDetails);
          setUploadedFileIsValid(false);
        },
        onSuccess: async (res) => {
          toaster.success({
            messageKey: "pages.simulering.lastet-opp-suksess",
          });
          setUploadedFileIsValid(true);
          setValidationResponse(res);
        },
        onSettled: () => {
          setFileUploadLoading(false);
        },
      },
    );
  };

  return (
    <div className={cn("space-y-6", className)}>
      <Card
        className={`border-dashed ${fileUploadLoading ? "animate-pulse motion-reduce:animate-none" : ""}`}
      >
        {fileUploadLoading === true ? (
          <span>{t("pages.simulering.laster-opp-fil")}</span>
        ) : (
          <div className="flex flex-row justify-between items-center gap-4">
            {uploadedFileName != null ? (
              !uploadedFileIsValid ? (
                <span>
                  {t("pages.simulering.opplasting-feil", {
                    context: uploadedFileName,
                  })}
                </span>
              ) : (
                <div className="flex gap-2 items-center">
                  <CheckmarkIcon fontSize={"1.5rem"} />
                  <span>
                    {t("pages.simulering.opplasting-ok", {
                      context: uploadedFileName,
                    })}
                  </span>
                </div>
              )
            ) : (
              <span>{t("pages.simulering.last-opp-fil")}</span>
            )}
            <UploadFileButton
              onFileChanged={handleUpload}
              acceptFileType=".csv"
              text={t("pages.simulering.last-opp-knapp")}
              className="bg-balder-support-surface-tinted! text-balder-text-support-default!"
            />
          </div>
        )}
      </Card>
      {fileUploadLoading !== true && fileUploadError != null && (
        <ValidationProblems
          validationProblemDetails={fileUploadError}
          maxErrorsToShow={5}
          message={t("pages.simulering.valideringsfeil-melding")}
        />
      )}
      {fileUploadLoading !== true &&
        fileUploadError == null &&
        validationResponse != null && (
          <MaaledataPeriodeOppsummering
            data={validationResponse}
            isTest={true}
          />
        )}
    </div>
  );
};
